import * as yaml from 'js-yaml'
import { JSONPath } from "jsonpath-plus"
import { IApplicationModel, K8SResourceType, KV, KVString, PlanContext, PluginDefinition, PluginInfoList } from 'yam-types'
import { APP_MODEL_FILES, YAM_PREFIX } from './constants'
import YamApplyContext from './context/apply'
import YamEngine from './engine'
import YamPluginComposer, { LifecycleHandlers, MergedHandler } from './engine/composer'
import YamStateStore, { YamMetadata } from './engine/store'
import KubernetesOperatorClient from './kube/kube-client'
import { ClientConfig, EngineConfig, EnvironmentDefinition } from './settings'
import TemplateRender from './template/render'
import type { KubernetesEnvironment, OperatorParam } from './types'
import { checkApplicationModelPath, createLogger, downloadUrl, fsRemove, fsRmdir, generateWorkloadNameWithEnv, isMultiEnvClusterMode, mergeObject, normalizeJsonPath, reduceWildcardEnvValue, sleep, unzip } from './util'

export type EnvPatternValues<V extends KV | unknown[]> = {
  envPattern: string,
  value: V
}

/**
 * MainOperator implements the major workflow of YAM runtime
 */
export class MainOperator {

  yamEngine: YamEngine
  pluginComposer: YamPluginComposer

  private templateRender: TemplateRender
  private kubeClient: KubernetesOperatorClient
  private jsonSchema: unknown
  private handlers: MergedHandler[]
  private lifecycleHandlers?: LifecycleHandlers
  private pluginInfoList: PluginInfoList

  private engineSettings: EngineConfig
  private builtInPlugins?: PluginDefinition<unknown>[]

  private envDisabledPlugins: EnvPatternValues<string[]>[]
  private envPluginsSettings: EnvPatternValues<KV>[]
  private envGroupVersionMappings: EnvPatternValues<KVString>[]
  private envGlobalValues: EnvPatternValues<{ [k: string]: KV }>[]

  private log = createLogger('main')

  constructor(engineSettings: EngineConfig, builtInPlugins?: PluginDefinition<unknown>[]) {

    const envDefs = envDefinitions(engineSettings, engineSettings.envDefinitions)
    this.envDisabledPlugins = envDefs.envDisabledPlugins
    this.envPluginsSettings = envDefs.envPluginsSettings
    this.envGroupVersionMappings = envDefs.envGroupVersionMappings
    this.envGlobalValues = envDefs.envGlobalValues

    this.engineSettings = engineSettings
    this.builtInPlugins = builtInPlugins
  }

  /**
   * The core operation process, cli will build parameters and call operate method
   */
  public async operate(param: OperatorParam): Promise<void> {
    // create new instance to avoid caching issue
    this.templateRender = new TemplateRender()
    this.pluginComposer = new YamPluginComposer(this.templateRender)

    for (const p of this.engineSettings.plugins) {
      if (p.builtIn) {
        const builtInPlugin = this.builtInPlugins?.find(x => x.name === p.name)
        if (builtInPlugin) {
          this.addPlugin(builtInPlugin)
        } else {
          this.log.error(`plugin ${p.name} is not built-in actually, skipped.`)
        }
      }
    }

    // check if it's applying from existing plan
    await this.checkAndResolvePlanFile(param)

    if (param.clusters.length === 0) {
      this.log.info('destination clusters are not specified, operation skipped.')
      return
    }
    const timestamp = Date.now()
    this.log.info('operation process started...')

    // a. read YAM definition from parameter or working directory
    let yamObj: IApplicationModel
    if (param.uninstallApp) {
      const tmp = param.uninstallApp.trim().split('/')
      if (tmp.length !== 2) {
        throw new Error(`invalid uninstall parameter: ${param.uninstallApp}, the format should be 'namespace/app-name'`)
      }
      yamObj = {
        metadata: {
          app: tmp[1].trim(),
          namespace: tmp[0].trim(),
        }
      }
    } else {
      const yamPath = checkApplicationModelPath(param.workingDir, this.templateRender.fileContentCache)
      yamObj = await this.templateRender.loadYaml(param.workingDir, yamPath) as IApplicationModel
      if (!yamObj) {
        throw new Error('can not resolve YAM file')
      }
      yamObj = this.templateMixInAndValidateMetadata(param.engine.clientConf || {}, yamObj)
    }

    // b. read dynamic values from 'values/' directory
    let customizedValues = new Map<string, KV>()
    if (param.runMode !== "uninstall") {
      const { values } = await this.templateRender.readCustomizedValues(
        param.workingDir, param.cmdParams, param.clusters, yamObj, param.engine.envLevelDefinitions,
        param.valueSources, param.engine.clientConf, this.envGlobalValues
      )
      customizedValues = values
    }

    // c. initialize kubernetes client and YAM engine
    this.kubeClient = await KubernetesOperatorClient.create(
      param.clusters, yamObj.metadata, param.dryRunMode || false, undefined,
      param.templateOutput, this.envGroupVersionMappings, param.engine, customizedValues
    )
    this.yamEngine = new YamEngine(this.templateRender, param, this.kubeClient)

    // d. plan and apply changes for each environment
    for (const cluster of param.clusters) {
      this.kubeClient.setCurrentContext(cluster)
      const customizedVal = customizedValues.get(cluster.name) || {}

      // e. merge plugin schema to build final json schema, plugin settings could be dynamic for each stack/env, and dynamic based on app model
      const inheritSettings = reduceWildcardEnvValue(cluster.name, this.envPluginsSettings) ?? {}

      const pluginSettings = JSON.parse(JSON.stringify(inheritSettings)) as KV
      const schemaHandlers = await this.pluginComposer.compose(
        param.engine.plugins,
        param,
        reduceWildcardEnvValue(cluster.name, this.envDisabledPlugins),
        pluginSettings,
        customizedVal
      )
      this.jsonSchema = schemaHandlers.jsonSchema
      this.handlers = schemaHandlers.handlers
      this.lifecycleHandlers = schemaHandlers.lifecycleHandlers
      this.pluginInfoList = schemaHandlers.pluginInfoList

      this.log.info(`processing environment [${cluster.name}] of stack [${cluster.stack}], got ${Object.keys(customizedVal).length} dynamic parameters.`)
      if (this.log.isLevelEnabled('debug')) {
        this.log.debug(`dynamic parameters are:\n${Object.keys(customizedVal).map(k => k + ': ' + JSON.stringify(customizedVal[k])).join('\n')}`)
      }

      if (param.runMode === "uninstall") {
        await this.uninstall(cluster, yamObj)
      } else {
        await this.planApply(param, cluster, customizedVal)
      }
    }
    this.log.info(`operation process finished, cost ${(Date.now() - timestamp) / 1000}s`)
  }


  /**
   * Add built-in plugins
   */
  public addPlugin(pluginDef: PluginDefinition<unknown>): void {
    this.pluginComposer.builtInPlugins.push(pluginDef)
  }

  private templateMixInAndValidateMetadata(clientConf: ClientConfig, yam: IApplicationModel): IApplicationModel {
    // mixin from templates when matching specific condition
    clientConf.templateSelector?.forEach(selector => {
      this.log.debug(`scan and merge mixin-model if '${selector.metaField}' matches '${selector.match}'`)
      if (selector.mixIn && selector.match && selector.metaField) {
        const matchArr: string[] = []
        if (selector.match instanceof Array) {
          matchArr.push(...selector.match)
        } else {
          matchArr.push(selector.match)
        }
        if (matchArr.includes(yam.metadata[selector.metaField] as string)) {
          for (const rawPath in selector.mixIn) {
            const preset = selector.mixIn[rawPath]
            if (!preset || typeof preset !== "object") {
              this.log.warn(`only object type preset fields are allowed when using mixin feature: ${rawPath}`)
              continue
            }
            const jsonPath = normalizeJsonPath(rawPath)
            const partialCurrent = JSONPath({ path: jsonPath, json: yam }) || []
            if (partialCurrent.length > 0) {
              this.log.info(`start merging mixin-model for '${rawPath}'`)
              for (const obj of partialCurrent) {
                if (typeof obj === "object") {
                  mergeObject(obj, JSON.parse(JSON.stringify(preset)), false, true)
                } else {
                  this.log.warn(`can not select when using mixin feature, selected path: ${rawPath}. mixin object ignored`)
                }
              }
            } else {
              this.log.warn(`mixin-model won't take effect because '${rawPath}' not exists in app model.`)
            }
          }
        }
      }
    })
    // validate required metadata fields
    clientConf.requiredMetaFields?.forEach(requiredMeta => {
      if (!yam.metadata[requiredMeta]) {
        throw new Error(`required metadata '${requiredMeta}' is missing for this app model`)
      }
    })
    return yam
  }

  /**
   * Plan-Apply is the abstraction of an operation process, by different CLI command, engine runs in different mode
   * plan-only  mode: "yam plan -v -c ..."
   * apply-only mode: "yam apply -f <file|url>"
   * plan-apply mode: "yam apply -version --clusters ..."
   * 
   * NOTE: running in 'apply-only' mode will still execute plan stage functions, but using the binary plan file.
   */
  private async planApply(param: OperatorParam, cluster: KubernetesEnvironment, customizedVal: KV): Promise<void> {
    // a. render current application model
    const yamPath = checkApplicationModelPath(param.workingDir, this.templateRender.fileContentCache)
    const current = await this.templateRender.renderTemplate(param.workingDir, yamPath, true, customizedVal)
    const currentYam = this.templateMixInAndValidateMetadata(param.engine.clientConf || {}, yaml.load(current) as IApplicationModel)
    this.log.info(`application model of [${currentYam.metadata.namespace}/${currentYam.metadata.app}] rendered with dynamic parameters.`)

    // a.1. for envs which enable multiEnvClusterMode means that multiple logical environments in one kubernetes cluster, will append env suffix
    const multiEnvMode = isMultiEnvClusterMode(cluster.name, param.engine.clientConf.multiEnvModeClusters)

    // b. find previous application model
    let yamMetadata = null
    if (!param.noDiff) {
      yamMetadata = await YamStateStore.loadYamMetadata(generateWorkloadNameWithEnv(currentYam.metadata.app, cluster.name), yamPath, this.kubeClient)
    }

    let previousYam = { metadata: currentYam.metadata }

    if (this.shouldDiff(yamMetadata)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.log.info(`previous application model fetched, last sync time: ${yamMetadata!.timestamp}`)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      previousYam = yamMetadata!.appModel
    } else {
      this.log.info(`running in no-diff mode, will replay all actions.`)
    }

    const planCtx = this.yamEngine.initPlanContext(previousYam, currentYam, this.pluginInfoList, cluster, customizedVal, this.engineSettings, multiEnvMode)
    if (param.filteredPlugins && param.filteredPlugins.length > 0) {
      planCtx.data.partialPluginMode = true
      this.log.info(`plugin filter specified, JSON schema validation will be skipped in this mode.`)
    }
    if (this.lifecycleHandlers?.beforePlan) {
      const beforePlan = this.lifecycleHandlers?.beforePlan
      for (const beforePlanFunc of beforePlan) {
        this.log.debug(`invoke beforePlan lifecycle hook: ${beforePlanFunc.name || '<anonymous>'}`)
        await beforePlanFunc.apply({}, [planCtx, {}])
        this.log.debug(`finish beforePlan lifecycle hook: ${beforePlanFunc.name || '<anonymous>'}`)
      }
    }

    // c. star Plan stage, pass the YAM data, and final merged json schema and operation handlers
    await this.yamEngine.plan(planCtx, this.jsonSchema, this.handlers)

    if (this.lifecycleHandlers?.afterPlan) {
      const afterPlan = this.lifecycleHandlers?.afterPlan
      for (const afterPlanFunc of afterPlan) {
        this.log.debug(`invoke afterPlan lifecycle hook: ${afterPlanFunc.name || '<anonymous>'}`)
        await afterPlanFunc.apply({}, [planCtx, {}])
        this.log.debug(`finish afterPlan lifecycle hook: ${afterPlanFunc.name || '<anonymous>'}`)
      }
    }

    // d. start Apply Stage
    if (param.runMode !== "plan-only") {
      const executeCtx = new YamApplyContext(this.kubeClient, !!param.dryRunMode, param.forceRestartWorkload, this.templateRender.preInjectedEnvVars.get(cluster.name))

      if (this.lifecycleHandlers?.beforeApply) {
        const beforeApply = this.lifecycleHandlers?.beforeApply
        for (const beforeApplyFunc of beforeApply) {
          this.log.info(`invoke beforeApply lifecycle hook: ${beforeApplyFunc.name}`)
          await beforeApplyFunc.apply({}, [executeCtx, {}])
          this.log.info(`finish beforeApply lifecycle hook: ${beforeApplyFunc.name}`)
        }
      }

      await this.yamEngine.apply(param, planCtx as PlanContext, executeCtx)

      if (this.lifecycleHandlers?.afterApply) {
        const afterApply = this.lifecycleHandlers?.afterApply
        for (const afterApplyFunc of afterApply) {
          this.log.info(`invoke afterApply lifecycle hook: ${afterApplyFunc.name}`)
          await afterApplyFunc.apply({}, [executeCtx, {}])
          this.log.info(`finish afterApply lifecycle hook: ${afterApplyFunc.name}`)
        }
        await sleep(1000)
      }
    }
  }


  private shouldDiff(yamMetadata: YamMetadata | null): boolean {
    if (!yamMetadata) {
      return false
    }
    const prevPluginList = yamMetadata.pluginInfoList
    const currPluginList = this.pluginInfoList

    const prevPluginListKeys = Object.keys(prevPluginList)
    const currPluginListKeys = Object.keys(currPluginList)

    if (prevPluginListKeys.length !== currPluginListKeys.length) {
      return false
    }

    return currPluginListKeys.every(pn =>
      prevPluginList[pn].version === currPluginList[pn].version
      && prevPluginList[pn].settingHash === currPluginList[pn].settingHash
    )
  }

  private async uninstall(cluster: KubernetesEnvironment, model: IApplicationModel): Promise<void> {
    const yamMetadata = await YamStateStore.loadYamMetadata(generateWorkloadNameWithEnv(model.metadata.app, cluster.name), APP_MODEL_FILES, this.kubeClient)
    if (yamMetadata) {
      await this.yamEngine.uninstall(yamMetadata, this.handlers, this.pluginInfoList, cluster)
    } else {
      throw new Error(`'${model.metadata.app}' of namespace '${model.metadata.namespace}' isn't installed in cluster ${cluster.name}`)
    }
  }

  private async checkAndResolvePlanFile(param: OperatorParam): Promise<void> {
    if (param.runMode === "apply-only" && param.planFile) {
      let planFilePath = param.planFile
      const needDownload = param.planFile.startsWith('http')
      if (needDownload) {
        const start = Date.now()
        this.log.info(`start downloading ${planFilePath} from ${param.planFile}`)
        const token = param.engine.serverConf?.token
        planFilePath = await downloadUrl(param.planFile, param.planFile.substring(param.planFile.lastIndexOf('/') + 1), token)
        this.log.info(`plan file downloaded, cost: ${(Date.now() - start) / 1000}s`)
      }
      try {
        // unarchive the plan to tmp directory, set param and file cache
        this.log.info(`retrieving application model and parameters from plan file.`)
        const tmpDir = await unzip(planFilePath)
        await YamStateStore.loadPlan(tmpDir, param, this.templateRender)
        this.log.info(`plan file loaded, will apply changes into these environment(s): ${param.clusters.map(r => r.rawClusterName).join(',')}`)
        await fsRmdir(tmpDir)
        if (needDownload) {
          await fsRemove(planFilePath)
        }
        this.log.debug(`tmp plan file/directory has been deleted.`)
      } catch (ex) {
        this.log.warn('can not retrieve release plan:' + ex.message)
        throw ex
      }
    }
  }

}

export function envDefinitions(
  engineSettings: EngineConfig,
  envDefinitions?: EnvironmentDefinition[]
): {
  envDisabledPlugins: EnvPatternValues<string[]>[],
  envPluginsSettings: EnvPatternValues<KV>[],
  envGroupVersionMappings: EnvPatternValues<KVString>[],
  envGlobalValues: EnvPatternValues<{ [k: string]: KV }>[]
} {
  const envDisabledPlugins: EnvPatternValues<string[]>[] = []
  const envPluginsSettings: EnvPatternValues<KV>[] = []
  const envGroupVersionMappings: EnvPatternValues<KVString>[] = []
  const envGlobalValues: EnvPatternValues<{ [k: string]: KV }>[] = []

  if (!envDefinitions) {
    return { envDisabledPlugins, envPluginsSettings, envGroupVersionMappings, envGlobalValues }
  }

  const clientConf = engineSettings.clientConf

  for (const idx in envDefinitions) {
    const envDefinition = envDefinitions[idx]
    const envPatterns = envDefinition.envPatterns ?? []
    if (envDefinition.envPattern) {
      envPatterns.push(envDefinition.envPattern)
    }
    if (envPatterns.length === 0) {
      continue
    }

    if (envDefinition.clusterInfo) {
      if (envDefinition.clusterInfo.kubeContextName) {
        if (!clientConf.contextPatternMapping) {
          clientConf.contextPatternMapping = {}
        }
        for (const envPattern of envPatterns) {
          clientConf.contextPatternMapping[envPattern] = envDefinition.clusterInfo.kubeContextName
        }
      }
      if (envDefinition.clusterInfo.kubeConfigPath) {
        if (!clientConf.kubeConfigFilePatternMapping) {
          clientConf.kubeConfigFilePatternMapping = {}
        }
        for (const envPattern of envPatterns) {
          clientConf.kubeConfigFilePatternMapping[envPattern] = envDefinition.clusterInfo.kubeConfigPath
        }
      }
    }

    for (const envPattern of envPatterns) {
      envDisabledPlugins.push({
        envPattern,
        value: envDefinition.disabledPlugins ?? []
      })
      envPluginsSettings.push({
        envPattern,
        value: envDefinition.settings ?? {}
      })
      envGroupVersionMappings.push({
        envPattern,
        value: envDefinition.apiVersionMapping ?? {}
      })
      envGlobalValues.push({
        envPattern,
        value: envDefinition.globalValues ?? {}
      })
    }
  }
  return {
    envDisabledPlugins, envPluginsSettings, envGroupVersionMappings, envGlobalValues
  }
}
